import { Outlet, type RouteObject } from 'react-router-dom';
import { RequireAuth } from '@/app/guards/RequireAuth';
import { RequireAccountEnabled } from '@/app/guards/RequireAccountEnabled';
import { RequireOnboardingComplete } from '@/app/guards/RequireOnboardingComplete';
import { AppShell } from '@/app/layout/AppShell';
import { LabChangeListener } from '@/app/layout/useSwitchActiveLab';
import { RootErrorBoundary } from '@/app/error/RootErrorBoundary';
import { FeatureErrorBoundary } from '@/app/error/FeatureErrorBoundary';
import { NotFound } from '@/app/error/NotFound';

/**
 * Pure, side-effect-free route tree — kept separate from `router.browser.ts` (which owns real
 * navigation side effects) so `router.test.tsx` can import it safely. `/registrierung`,
 * `/login` and `/passwort-vergessen` are deliberately top-level siblings, never nested under the
 * guarded `/` route — they are the only unauthenticated entry points into the portal.
 * `/profil`, `/meine-organisation`,
 * `/datenschutz`, `/agb` and `/impressum` are nested under `/` so they share both the
 * `RequireAuth` guard and the persistent `AppShell` chrome (header/footer) with every other route
 * — a route change only swaps `<Outlet />`'s `main` content (e01-03-ak-1), it never remounts the
 * shell.
 *
 * `/onboarding` is its own top-level route, a sibling of `/login` and `/registrierung`, so it
 * renders bare/full-screen — no `AppShell`, no navbar, no footer — a seamless continuation of the
 * login screen. It still requires a session, so it keeps its own `RequireAuth` wrapper
 * (e10-01-ak-5) instead of inheriting the one on `/`; it deliberately does NOT sit inside
 * `RequireOnboardingComplete`, since that guard's job is to redirect *into* `/onboarding`, not to
 * gate the page itself.
 *
 * `RequireAccountEnabled` wraps the `/` subtree, inside `RequireAuth` and ahead of
 * `RequireOnboardingComplete`: it redirects a principal whose active organization is `DISABLED`
 * to `/account-deaktiviert` on any entry into a business route — deep-link, reload, back/forward
 * — not just the post-login redirect (US-E10-06 AK-5/AK-8). `/account-deaktiviert` is its own
 * top-level route, a sibling of `/onboarding`, rendering bare/full-screen (no `AppShell`) behind
 * its own `RequireAuth` wrapper — for the same reasons `/onboarding` is one.
 *
 * `RequireOnboardingComplete` wraps the `/` subtree, inside `RequireAuth`: it redirects a
 * principal with `onboardingRequired` to `/onboarding` on any entry into a business route —
 * deep-link, reload, back/forward — not just the post-login redirect (US-E10-06 AK-6).
 *
 * `/auftraege` and `/auftraege/:type/:id` are lazy-loaded under the shared `RequireAuth` guard.
 * `/auftraege/:type/:id` resolves to `AuftragDetailRoute`, a thin wrapper that reads only `id`
 * from the URL and renders `AuftragDetailShell` unconditionally — the `:type` segment is never
 * used for branching (see `AuftragDetailRoute`'s own doc comment).
 *
 * `/auftraege/ortho/:id/aligner` is the separate E20 Aligner detail page, registered as a static
 * sibling ahead of `/auftraege/:type/:id` — React Router ranks a static path segment (`ortho`,
 * `aligner`) above the purely dynamic `:type` segment regardless of declaration order, so an
 * Aligner order's own detail route and the generic order-detail route never collide despite the
 * matching depth. `AuftragDetailShell` redirects an `ORTHO_ALIGNER`-variant order here via
 * `<Navigate replace>` once it has resolved the order's actual type server-side.
 *
 * `/auftraege/erstellen` (Auftragsart-Auswahl, US-E02-01), `/auftraege/erstellen/vergabe`
 * (Auftragsvergabe pre-step, US-E02-02, generalized beyond its original Prothetik-only scope),
 * `/auftraege/erstellen/daten` (Auftragsdaten Schritt 1, US-E02-03),
 * `/auftraege/erstellen/uploads` (Auftragsdaten Schritt 2, Datei-Upload),
 * `/auftraege/erstellen/liefertermin` (Schritt 3, Liefertermin & Versandoptionen) and
 * `/auftraege/erstellen/zusammenfassung` (Schritt 4, Zusammenfassung & Einreichung, US-E02-06)
 * are registered as static siblings of `/auftraege/:type/:id` — React
 * Router ranks static path segments above dynamic ones, so there is no ambiguity despite the
 * matching depth. `/auftraege/erstellen/interne-vergabe` is kept registered as a deprecated alias
 * of `/auftraege/erstellen/vergabe` (same lazy module) until the release freeze, for any
 * already-shared link/bookmark to the old Prothetik-only path.
 *
 * `/support-dashboard` is nested under the same guarded root route as every other authenticated
 * route — it needs no route-level guard of its own beyond `RequireAuth`, because its own
 * role-based allow-list gating (`RequireAnyRole`) is applied inside `SupportDashboardRoute`
 * itself, not here.
 *
 * `/kunden` is the single shared list route for both the DELABO-Portal Admin/Support audience
 * and the DELABO-Labor GF/Leitung/Verwaltung audience — the audience distinction
 * is made inside `CustomersRoute` itself (by actor org-type, behind its own `RequireAnyRole`), not
 * by a separate path. `/kunden/erstellen` (US-E07-03) is the proxy customer-creation CreatePage,
 * gated by its own, narrower `RequireAnyRole` (Anlage-Rollen only — excludes
 * `DELABO_LAB_EMPLOYEE`, which may view the list but not create). `/kunden/:id/bearbeiten` (US-E09-01)
 * is the EditPage, entered only via the customer list's own "Bearbeiten" row action (AK-7) — never
 * an in-page organization picker — and gated by its own, narrower `RequireAnyRole` (the same
 * allow-list as `/kunden/erstellen`). `/kunden/:id` is the view-first DetailPage a customer-list
 * row click lands on — it renders the same page body as the EditPage but read-only, with its own,
 * wider `RequireAnyRole` (`CUSTOMERS_ALLOWED_ROLES`, the list's own view allow-list) and a
 * "Bearbeiten" link into `/kunden/:id/bearbeiten` gated by the narrower edit allow-list. Registered
 * as static siblings ahead of `/kunden/:id` for readability —
 * React Router already ranks a static path segment (`erstellen`, `:id/bearbeiten`) above a purely
 * dynamic one (`:id`) regardless of declaration order. German path segments throughout, consistent
 * with the portal's other
 * business routes (`/auftraege`, `/auftraege/erstellen`, `/registrierung`, `/profil`; QK-SW-19) —
 * neither `/customers` nor a nested `/verwaltung/kunden`.
 *
 * `/labore` is the DELABO-Portal-only laboratory-overview list (`PORTAL_ADMIN`/`PORTAL_SUPERUSER`,
 * gated inside `LaboratoriesRoute` itself, not by a route-level guard here) — the top-nav "Labore"
 * item now targets it instead of `/coming-soon`. `/labore/erstellen` is the laboratory-creation
 * CreatePage, gated by its own, narrower `RequireAnyRole` (`PORTAL_SUPERUSER`/`PORTAL_ADMIN`
 * only — `DELABO_LAB_CEO` is excluded, since laboratory creation exposes cross-laboratory
 * Geschäftsführer-candidate PII this role has no legitimate need to see) — registered as a static
 * sibling ahead of `/labore/:id` for the same static-before-dynamic reason as `/kunden/erstellen`.
 * `/labore/:id/bearbeiten` (US-E07-02) is the EditPage, entered only via the detail page's own
 * "Bearbeiten" link — never an in-page organization picker — and gated by its own, narrower
 * `RequireAnyRole` (`LABORATORY_EDIT_ALLOWED_ROLES`). `/labore/:id` is the view-first DetailPage a
 * laboratory-list row click lands on — it renders the same page body as the EditPage but read-only,
 * with its own, wider `RequireAnyRole` (`LABORATORY_DETAIL_ALLOWED_ROLES`) and a "Bearbeiten" link
 * into `/labore/:id/bearbeiten` gated by the narrower edit allow-list. Registered as a static
 * sibling ahead of `/labore/:id` for the same static-before-dynamic reason as
 * `/kunden/:id/bearbeiten`.
 *
 * `/meine-organisation` is the self-service single-resource EditPage a Praxis/Kundenlabor
 * organisation uses to view (any member) and, for GF/Leitung roles only, edit its own master
 * data — the `UserMenu` entry that links here replaces the former `/kontaktdaten` stub.
 *
 * `/mein-laborprofil` is the self-service single-resource EditPage a DELABO laboratory uses to
 * view (all three laboratory roles) and, for GF/Leitung roles only, edit its own master data
 * (address, Bundesland/Land, contact person, logo) — the `UserMenu` entry linking here is visible
 * to all three laboratory role tiers, unlike `/meine-organisation`'s LEAD-and-above gate, because
 * the read path here is wider. No route-level guard here either: `MeinLaborprofilRoute` renders
 * its own `access_denied` state for a `403` (a non-`DELABO_LAB` actor or an unmapped role), the
 * same in-page gating convention as `/meine-organisation`.
 *
 * `/benutzer/erstellen` (US-E22-02) is the Mitarbeiter-CreatePage, gated by its own
 * `RequireAnyRole` (Portal-Rollen only) inside `CreateUserRoute`. German path segments here too,
 * matching `/kunden/erstellen` and the rest of the portal's business routes (QK-SW-19).
 *
 * `/mitarbeiter/erstellen` is the customer self-service counterpart, gated by its own
 * `RequireAnyRole` (the four Praxis/Kundenlabor Creator-Rollen only) inside
 * `CreateCustomerUserRoute`. Deliberately a distinct path from `/benutzer/erstellen` rather than
 * the same path reused for both audiences — the admin route already owns that path with its own,
 * differently-scoped `RequireAnyRole`, and two `RouteObject`s cannot share one path under the same
 * parent (only the first-declared would ever be reachable).
 *
 * `/logistik` is the Logistik area screen (Pencil node `Hed0T`) — the Versandauftrags-Übersicht
 * with one sicht each for DELABO-lab, customer (Praxis/Kundenlabor) and DELABO-Portal principals,
 * and the parent segment of `/logistik/lieferdienstverwaltung` below. No route-level guard here:
 * `LogistikUebersichtRoute` gates itself with a fail-closed role×organization-type check per sicht
 * (no separate role allow-list). The top-nav "Logistik" item targets it instead of `/coming-soon`
 * (see `navItems.ts`), and stays marked active on this route and every `/logistik/*` sub-route
 * (`AppTopNav`'s `NavLink`).
 *
 * `/logistik/lieferdienstverwaltung` (US-E15-01) is the settings/tab page for DHL Express/UPS
 * delivery-carrier credentials, gated by its own `RequireAnyRole` inside
 * `LieferdienstverwaltungRoute` itself (view open to all three DELABO-lab roles and, for the
 * centrally-managed UPS access, `PORTAL_ADMIN`/`PORTAL_SUPERUSER` as well; the narrower write
 * paths per mode are in-page checks — see `logistikRoles.ts`) — no route-level guard here, same
 * convention as `/support-dashboard` and `/labor/mitarbeiter/erstellen`. Reached from `/logistik`
 * via the "Versanddienstleister verwalten" button for DELABO-lab and `DELABO_PORTAL` principals
 * alike — no longer from a `UserMenu` entry (removed as redundant once the nav item pointed at the
 * area screen).
 *
 * `/logistik/versandauftraege/neu` is the three-step shipment-pickup capture wizard
 * (`VersandauftragErstellenRoute`, Pencil node `hfG8k`) — unlike every other `/logistik/*` route,
 * its own `RequireAnyRole` allow-list (`VERSANDAUFTRAG_ERSTELLEN_ALLOWED_ROLES`) is **not**
 * DELABO-lab-only: a Praxis/Kundenlabor actor may book its own pickup directly (FK 1.4), so the
 * three customer-role tiers for both customer organization kinds are unioned onto the three
 * DELABO-lab roles. No route-level guard here — the same in-route-guard convention as its sibling
 * routes. Reached from `/logistik` via the "Neuer Versandauftrag" button (DELABO-lab principals
 * only, from that screen); a customer principal has no top-nav entry pointing here yet — only a
 * direct URL visit, which the wizard's own guard still authorizes per FK 1.4.
 *
 * `/logistik/versandauftraege/:id` (US-E15-10, `VersandauftragDetailRoute`, Pencil node `oig3G`)
 * is the shipment order's own detail page — registered as a static-before-dynamic sibling after
 * `/logistik/versandauftraege/neu` so the literal `neu` segment is never swallowed by `:id`. (React
 * Router already ranks a static path segment above a dynamic one at the same depth regardless of
 * declaration order, same reasoning as `/kunden/erstellen` vs. `/kunden/:id` above — the ordering
 * here is for table readability only.) The route reads only `id` and hands it unchanged to
 * `VersandauftragDetailPage`; like the overview it has no role allow-list (every portal role sees
 * shipment orders in some form), only a no-principal `AccessDenied` — a purely informative gate; the
 * binding authorization is the server's `VersandauftragVisibility.forActor`, which returns the
 * same non-disclosure `404` for "does not exist" and "foreign tenant" alike. Reached from the
 * overview's row menu ("Details", work-item 10) and from the wizard's post-submit redirect
 * (work-item 11); `DELABO_PORTAL` support reaches it only via a direct link and sees a read-only
 * action bar once there.
 *
 * `/labor/mitarbeiter/erstellen` (A.3, merging #514/US-E22-03 and #515/US-E22-04) is the single
 * DELABO-lab user-creation CreatePage, gated by `RequireAnyRole` (the two DELABO-lab leadership
 * roles plus `DELABO_LAB_EMPLOYEE`) inside `CreateDelaboLabScopedUserRoute`. One target-selection
 * combobox calls one endpoint (`GET /api/v1/labor/benutzer-organisationen`) that returns the caller's own
 * DELABO-lab location(s) and, for leadership roles, their assigned customers as a single
 * fail-closed union — role vocabulary, marketing-consent visibility and the partner-lab line all
 * derive from each row's `OrganizationType`, not from a separate "Zielart" choice or a second
 * route. Lives under its own `labor/` segment rather than reusing `/benutzer/erstellen` or
 * `/mitarbeiter/erstellen`, since neither existing path's `RequireAnyRole` allow-list matches this
 * audience/scope combination. This is the route's only registered path — the pre-merge
 * `/labor/benutzer/erstellen` (#514) and `/labor/kunden/benutzer/erstellen` (#515) paths are
 * removed without a redirect or alias (fixed product decision, no backward-compat guarantee for
 * either pre-merge bookmark).
 *

 * The `*` catch-all stays a plain `<NotFound />`: `RequireAnyRole` gained an additive, opt-in
 * `redirectToRoleHome` variant (AC9) for routes that want to send an authenticated-but-wrong-role
 * visitor to their own role-home instead of an `AccessDenied` page, but that is a per-route guard
 * decision, not something this catch-all can make — by the time an authenticated user reaches
 * `*`, the path genuinely doesn't correspond to any registered route, and 404 is the correct
 * response (Nutzer-Entscheidung 4: "echte unbekannte Pfade zeigen weiterhin die 404-Seite").
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RequireAuth>
        <RequireAccountEnabled>
          <RequireOnboardingComplete>
            <LabChangeListener />
            <AppShell />
          </RequireOnboardingComplete>
        </RequireAccountEnabled>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        lazy: () => import('@/features/dashboard/routes/DashboardRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege',
        lazy: () => import('@/features/auftraege/routes/AuftraegeRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen',
        lazy: () => import('@/features/auftragsanlage/routes/AuftragsartAuswahlRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen/vergabe',
        lazy: () => import('@/features/auftragsanlage/routes/AuftragsvergabeRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        // Deprecated alias — kept until the release freeze so an already-shared link/bookmark to
        // the old Prothetik-only path keeps working. Resolves to the same generalized
        // `AuftragsvergabeRoute`; no separate component is maintained for this path.
        path: 'auftraege/erstellen/interne-vergabe',
        lazy: () => import('@/features/auftragsanlage/routes/AuftragsvergabeRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen/daten',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftragsanlage/routes/AuftragsdatenRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen/uploads',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftragsanlage/routes/UploadsRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen/liefertermin',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftragsanlage/routes/LieferterminRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/erstellen/zusammenfassung',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftragsanlage/routes/ZusammenfassungRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/ortho/:id/aligner',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftraege/aligner/routes/AlignerDetailRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'auftraege/:type/:id',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/auftraege/routes/AuftragDetailRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'kunden',
        lazy: () => import('@/features/customers/routes/CustomersRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'kunden/erstellen',
        lazy: () => import('@/features/customers/routes/CreateCustomerRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'kunden/:id/bearbeiten',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/customers/routes/EditCustomerRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'kunden/:id',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/customers/routes/CustomerDetailRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'labore',
        lazy: () => import('@/features/labore/routes/LaboratoriesRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'labore/erstellen',
        lazy: () => import('@/features/labore/routes/CreateLaboratoryRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'labore/:id/bearbeiten',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/labore/routes/EditLaboratoryRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'labore/:id',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/labore/routes/LaboratoryDetailRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'benutzer',
        lazy: () => import('@/features/benutzer/routes/BenutzerRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'benutzer/erstellen',
        lazy: () => import('@/features/benutzer/routes/CreateUserRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'benutzer/:userId/bearbeiten',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/benutzer/routes/EditBenutzerRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'mitarbeiter/erstellen',
        lazy: () => import('@/features/benutzer/routes/CreateCustomerUserRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'labor/mitarbeiter/erstellen',
        lazy: () => import('@/features/benutzer/routes/CreateDelaboLabScopedUserRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'profil',
        lazy: () => import('@/features/account/routes/ProfileRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'meine-organisation',
        lazy: () => import('@/features/organisation/routes/MeineOrganisationRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'mein-laborprofil',
        lazy: () => import('@/features/laborprofil/routes/MeinLaborprofilRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'logistik',
        lazy: () => import('@/features/logistik/routes/LogistikUebersichtRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'logistik/lieferdienstverwaltung',
        lazy: () => import('@/features/logistik/routes/LieferdienstverwaltungRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'logistik/versandauftraege/neu',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/logistik/routes/VersandauftragErstellenRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'logistik/versandauftraege/:id',
        handle: { kontextgebunden: true },
        lazy: () => import('@/features/logistik/routes/VersandauftragDetailRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'dateiablage',
        lazy: () => import('@/features/dateiablage/routes/DateiablageRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'datenschutz',
        lazy: () => import('@/features/legal/routes/PrivacyRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'agb',
        lazy: () => import('@/features/legal/routes/TermsRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'impressum',
        lazy: () => import('@/features/legal/routes/ImprintRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: 'coming-soon',
        lazy: () => import('@/features/shell/routes/ComingSoonRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      // INC-KW42-14: one placeholder path per top-nav entry without a real target yet, so NavLink's
      // path-prefix active state marks exactly one entry. All render the shared ComingSoonRoute; a
      // path is replaced by the real target route once its feature ships. The bare `coming-soon`
      // above stays for existing bookmarks (and footer/dashboard links).
      ...(['verwaltung', 'rechnungen', 'akademie', 'logistik'] as const).map((bereich) => ({
        path: `coming-soon/${bereich}`,
        lazy: () => import('@/features/shell/routes/ComingSoonRoute'),
        errorElement: <FeatureErrorBoundary />,
      })),
      {
        path: 'support-dashboard',
        lazy: () => import('@/features/support-dashboard/routes/SupportDashboardRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: '/login',
    lazy: () => import('@/features/auth/routes/LoginRoute'),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: '/registrierung',
    lazy: () => import('@/features/auth/routes/RegisterRoute'),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: '/passwort-vergessen',
    lazy: () => import('@/features/auth/routes/ForgotPasswordRoute'),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: '/onboarding',
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        lazy: () => import('@/features/onboarding/routes/OnboardingRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
    ],
  },
  {
    path: '/account-deaktiviert',
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        lazy: () => import('@/features/auth/routes/AccountDisabledRoute'),
        errorElement: <FeatureErrorBoundary />,
      },
    ],
  },
];
